/**
 * Applications table — shared by the Applications list and the Job Opening detail.
 * Columns: Candidate · Job Opening · Stage · Source · Recruiter · Applied · Last activity.
 */

import React, { useMemo } from 'react';
import { Table, TableColumn } from '../../../components/ui/DataDisplay';
import type { ApplicationRow } from '../../../services/recruitmentApi';
import { StageBadge, SourcePill } from './badges';
import { formatDate, timeAgo, recruiterName } from '../utils/format';

interface ApplicationsTableProps {
	rows: ApplicationRow[];
	onOpen: (applicationId: number) => void;
	/** Hide the Job Opening column (e.g. on a single job's detail page). */
	hideJobColumn?: boolean;
}

export const ApplicationsTable: React.FC<ApplicationsTableProps> = ({ rows, onOpen, hideJobColumn }) => {
	const columns: TableColumn<Record<string, unknown>>[] = useMemo(() => {
		const cols: TableColumn<Record<string, unknown>>[] = [
			{
				key: 'candidate_name',
				title: 'Candidate',
				align: 'left',
				render: (_v, record) => {
					const r = record as unknown as ApplicationRow;
					return <span className='font-semibold text-gray-900'>{r.candidate_name}</span>;
				},
			},
		];

		if (!hideJobColumn) {
			cols.push({
				key: 'job_title',
				title: 'Job Opening',
				align: 'left',
				render: (_v, record) => <span className='text-gray-700'>{(record as unknown as ApplicationRow).job_title}</span>,
			});
		}

		cols.push(
			{
				key: 'stage',
				title: 'Stage',
				align: 'center',
				render: (_v, record) => <StageBadge stage={(record as unknown as ApplicationRow).stage} />,
			},
			{
				key: 'source',
				title: 'Source',
				align: 'center',
				render: (_v, record) => <SourcePill source={(record as unknown as ApplicationRow).source} />,
			},
			{
				key: 'recruiter',
				title: 'Recruiter',
				align: 'left',
				render: (_v, record) => {
					const r = record as unknown as ApplicationRow;
					return <span className='text-gray-700'>{recruiterName(r.recruiter_first_name, r.recruiter_last_name)}</span>;
				},
			},
			{
				key: 'applied_date',
				title: 'Applied',
				align: 'center',
				render: (_v, record) => <span className='text-gray-700'>{formatDate((record as unknown as ApplicationRow).applied_date)}</span>,
			},
			{
				key: 'updated_at',
				title: 'Last activity',
				align: 'center',
				render: (_v, record) => <span className='text-gray-500'>{timeAgo((record as unknown as ApplicationRow).updated_at)}</span>,
			}
		);

		return cols;
	}, [hideJobColumn]);

	return (
		<Table
			columns={columns}
			data={rows as unknown as Record<string, unknown>[]}
			size='sm'
			onRowClick={record => onOpen((record as unknown as ApplicationRow).id)}
		/>
	);
};
